/**
 * "New booking" / "Move" (designs DlgBooking, PlannerStates; LIAA service plan S6 — P1-7): the work order — an
 * existing one, or a new one made here ("Create and book") — the workplace, the start (a Riga day and a quarter hour)
 * and the duration, and the performer among those free in the slot, or "Later". A tyre fitting shows the client's and
 * the car's tyre sets with their address and debt, or the way to receive one. The server decides: a conflict comes back
 * with what is in the way and, when the day allows, "Start at HH:MM".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, DatePicker, Flex, Input, Modal, Radio, Segmented, Select, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import * as planner from './plannerApi';
import { exactInstant, hhmm, riga } from './utils/time';
import { workOrderKeys } from '../api/queryKeys';
import * as api from '../api/workOrdersApi';
import { formatMoney } from '../utils/format';
import { useWorkOrderError } from '../utils/useWorkOrderError';

import type { Booking, BookingConflict, PlannerTireSet } from './plannerApi';

import { localName, useWorkTypes } from '@/features/service-setup';
import { describeApiError } from '@/shared/api/error';
import { useAuth } from '@/shared/auth/AuthProvider';

const QUIET = { suppressGlobalError: true } as const;
const TIMES = Array.from({ length: 96 }, (_, i) => i * 15);
const DURATIONS = [15, 30, 45, 50, 60, 75, 90, 120, 150, 180, 240, 300, 360, 420, 480, 540];

export interface BookingDialogProps {
  /** Moving an existing booking; else a new one. */
  booking?: Booking | null;
  /** The order is fixed (the waiting list, the order card). */
  order?: { id: string; label: string } | null;
  initial: { day: string; minutes: number; duration: number; resourceId: string | null; staffId: string | null };
  resources: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: (booking: Booking) => void;
}

/** The words of a refusal: what is in the way, one sentence each. */
export function useConflictText(): (c: BookingConflict, names: { resource: string; staff: string }) => string {
  const { t } = useTranslation('workOrders');
  return (c, names) => {
    const span = c.startsAt !== undefined && c.endsAt !== undefined ? `${hhmm(riga(c.startsAt).minutes)}–${hhmm(riga(c.endsAt).minutes)}` : '';
    const who = c.plate ?? c.orderNumber ?? '';
    switch (c.reason) {
      case 'resource-busy':
        return t('planner.conflict.resourceBusy', { name: names.resource, span, who });
      case 'staff-busy':
        return t('planner.conflict.staffBusy', { name: names.staff, span, who });
      case 'staff-absent':
        return t('planner.conflict.staffAbsent', { name: names.staff, kind: t(`planner.absence.${c.absenceKind ?? 'other'}`) });
      case 'staff-off-hours':
        return c.hours == null
          ? t('planner.conflict.staffDayOff', { name: names.staff })
          : t('planner.conflict.staffHours', { name: names.staff, span: `${c.hours.startsAt}–${c.hours.endsAt}` });
      case 'staff-inactive':
        return t('planner.conflict.inactive', { name: names.staff });
      case 'resource-inactive':
        return t('planner.conflict.inactive', { name: names.resource });
    }
  };
}

export function BookingDialog({ booking = null, order = null, initial, resources, onClose, onDone }: BookingDialogProps): React.JSX.Element {
  const { t, i18n } = useTranslation(['workOrders', 'service']);
  const describe = useWorkOrderError();
  const conflictText = useConflictText();
  const queryClient = useQueryClient();
  const { hasCapability } = useAuth();
  const canCreateOrder = hasCapability('service.wo.write');
  const [current, setCurrent] = useState<Booking | null>(booking);
  const moving = current !== null;

  const [source, setSource] = useState<'existing' | 'new'>('existing');
  const [orderId, setOrderId] = useState<string | null>(order?.id ?? booking?.workOrderId ?? null);
  const [orderQ, setOrderQ] = useState('');
  // What the fields started from — the booking as opened, or as reloaded (an unchanged start keeps its instant).
  const [base, setBase] = useState(initial);
  const [resourceId, setResourceId] = useState<string | null>(initial.resourceId);
  const [day, setDay] = useState(initial.day);
  const [minutes, setMinutes] = useState(initial.minutes);
  const [duration, setDuration] = useState(initial.duration);
  const [staffId, setStaffId] = useState<string | null>(initial.staffId);
  const [created, setCreated] = useState<{ id: string; label: string } | null>(null);
  // A new order's fields.
  const [vehicleQ, setVehicleQ] = useState('');
  const [vehicle, setVehicle] = useState<{ id: string; label: string; ownerId: string | null; ownerName: string | null } | null>(null);
  const [customerQ, setCustomerQ] = useState('');
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [workTypeId, setWorkTypeId] = useState<string | null>(null);
  const [request, setRequest] = useState('');
  const [refusal, setRefusal] = useState<{ conflicts: BookingConflict[]; freeFrom: string | null } | { message: string; stale?: boolean } | null>(null);

  // A moved booking keeps its own start while the start is not touched (an ambiguous autumn-night time stays the one
  // it is); a new start is the Riga wall-clock time, or null when that time does not exist (the spring gap). The end is
  // the start plus the real duration — never wall-clock addition across a clock change.
  const startsAt = current !== null && day === base.day && minutes === base.minutes ? current.startsAt : exactInstant(day, minutes);
  const endsAt = startsAt === null ? null : new Date(Date.parse(startsAt) + duration * 60_000).toISOString();
  const workTypes = useWorkTypes(false);
  const orders = useQuery({
    queryKey: [...workOrderKeys.all, 'planner-orders', orderQ],
    queryFn: () => api.fetchWorkOrders({ page: 1, limit: 20, view: 'open', sort: 'number', dir: 'desc', ...(orderQ.trim() !== '' && { q: orderQ.trim() }) }),
    enabled: !moving && order === null && created === null && source === 'existing',
    meta: QUIET,
  });
  const vehicles = useQuery({ queryKey: workOrderKeys.lookup('vehicles', vehicleQ), queryFn: () => api.lookupVehicles(vehicleQ), enabled: source === 'new' && vehicleQ.trim().length >= 2, meta: QUIET });
  const customers = useQuery({ queryKey: workOrderKeys.lookup('customers', customerQ), queryFn: () => api.lookupCustomers(customerQ), enabled: source === 'new' && customerQ.trim().length >= 2, meta: QUIET });
  const free = useQuery({
    queryKey: planner.plannerKeys.free(startsAt ?? '', endsAt ?? '', current?.id ?? null),
    queryFn: () => planner.fetchFreeStaff(startsAt as string, endsAt as string, current?.id ?? null),
    enabled: startsAt !== null,
    meta: QUIET,
  });
  const fixedOrder = created ?? order;
  // The order whose tyre sets show: the made one, the fixed one, or the picked existing one — never one picked before
  // switching to "New".
  const effectiveOrderId = created?.id ?? order?.id ?? (source === 'existing' ? orderId : null);
  const tires = useQuery({ queryKey: planner.plannerKeys.tireSets(effectiveOrderId ?? ''), queryFn: () => planner.fetchOrderTireSets(effectiveOrderId as string), enabled: effectiveOrderId !== null && !moving, meta: QUIET });
  // The order's owner, for "Receive a tyre set" — read only when that link is shown.
  const card = useQuery({ queryKey: workOrderKeys.card(effectiveOrderId ?? ''), queryFn: () => api.fetchWorkOrder(effectiveOrderId as string), enabled: effectiveOrderId !== null && !moving && tires.data?.sets.length === 0 && tires.data.tyreFitting, meta: QUIET });
  const newFitting = workTypes.data?.find((w) => w.id === workTypeId)?.code === 'tyre-fitting';
  // A new order's sets before it exists: from the tyre storage, for whoever reads it.
  const pendingNew = !moving && effectiveOrderId === null && source === 'new' && (customer !== null || vehicle !== null);
  const newTires = useQuery({
    queryKey: [...planner.plannerKeys.all, 'new-tire-sets', customer?.id ?? null, vehicle?.id ?? null],
    queryFn: () => planner.fetchTireSetsOf({ customerId: customer?.id ?? null, vehicleId: vehicle?.id ?? null }),
    enabled: pendingNew && hasCapability('tire-storage.read'),
    meta: QUIET,
  });

  const workTypeName = (id: string): string => {
    const wt = workTypes.data?.find((w) => w.id === id);
    return wt === undefined ? '' : localName(wt, i18n.language);
  };
  const orderLabel = (o: { number: string; vehicle: { plate: string | null; make: string | null; model: string | null } | null; workTypeId: string }): string =>
    [o.number, o.vehicle?.plate, [o.vehicle?.make, o.vehicle?.model].filter(Boolean).join(' '), workTypeName(o.workTypeId)].filter((p) => p != null && p !== '').join(' · ');

  const staffName = (id: string | null): string => (id === null ? '' : (free.data?.find((s) => s.id === id)?.name ?? current?.staffName ?? ''));
  const resourceName = (id: string | null): string => resources.find((r) => r.id === id)?.name ?? '';

  const finish = (b: Booking): void => {
    void queryClient.invalidateQueries({ queryKey: planner.plannerKeys.all });
    void queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
    onDone(b);
  };
  const refuse = (error: unknown): void => {
    // Whatever happened, the planner shows the server's state again.
    void queryClient.invalidateQueries({ queryKey: planner.plannerKeys.all });
    const info = describeApiError(error);
    if (info.code === 'VERSION_CONFLICT') setRefusal({ message: describe(error), stale: true });
    else if (info.code === 'BOOKING_CONFLICT') {
      const d = info.details as { conflicts?: BookingConflict[]; freeFrom?: string | null } | undefined;
      setRefusal({ conflicts: d?.conflicts ?? [], freeFrom: d?.freeFrom ?? null });
    } else setRefusal({ message: describe(error) });
  };
  /** The booking as it is now on the server — after "changed by someone else". */
  const reload = async (): Promise<void> => {
    if (current === null) return;
    const fresh = (await planner.fetchOrderBookings(current.workOrderId)).find((b) => b.id === current.id);
    if (fresh !== undefined) {
      // Every field from the booking as it is now — the dialog never mixes its old slot with the new version.
      const s = riga(fresh.startsAt);
      const next = { day: s.day, minutes: s.minutes, duration: Math.round((Date.parse(fresh.endsAt) - Date.parse(fresh.startsAt)) / 60_000), resourceId: fresh.resourceId, staffId: fresh.staffId };
      setCurrent(fresh);
      setBase(next);
      setDay(next.day);
      setMinutes(next.minutes);
      setDuration(next.duration);
      setResourceId(next.resourceId);
      setStaffId(next.staffId);
    }
    setRefusal(null);
  };
  const save = useMutation({
    mutationFn: async (): Promise<Booking> => {
      setRefusal(null);
      const slot = { resourceId, staffId, startsAt: startsAt as string, endsAt: endsAt as string };
      if (current !== null) return planner.updateBooking(current.id, { version: current.version, ...slot });
      let target = effectiveOrderId;
      if (source === 'new' && created === null) {
        const card = await api.createWorkOrder({
          workTypeId: workTypeId as string,
          vehicleId: vehicle?.id ?? null,
          customerId: (customer as { id: string }).id,
          payerCustomerId: null,
          responsibleUserId: null,
          requestText: request.trim(),
          dueOn: null,
          urgent: false,
          lines: [],
        });
        // Made once: a refused booking keeps the order, and "Book" tries again on it. The lists know of it at once.
        setCreated({ id: card.id, label: orderLabel(card) });
        target = card.id;
        void queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      }
      return planner.createBooking(target as string, slot);
    },
    onSuccess: finish,
    onError: refuse,
    meta: QUIET,
  });

  const newReady = source === 'new' && created === null;
  const orderReady = moving || fixedOrder !== null || (source === 'existing' ? orderId !== null : customer !== null && workTypeId !== null && request.trim() !== '');
  const nobody = resourceId === null && staffId === null;
  const freeList = free.data ?? [];
  const staffChoices = staffId !== null && !freeList.some((s) => s.id === staffId) ? [...freeList, { id: staffId, name: staffName(staffId) || initial.staffId || '', busy: true }] : freeList;

  return (
    <Modal
      open
      width={640}
      title={moving ? t('planner.dialog.moveTitle', { what: current?.vehicle?.plate ?? current?.orderNumber }) : t('planner.dialog.title')}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>{t('planner.dialog.cancel')}</Button>
          <Button type="primary" disabled={!orderReady || nobody || duration <= 0 || startsAt === null} loading={save.isPending} onClick={() => save.mutate()}>
            {moving ? t('planner.dialog.save') : newReady ? t('planner.dialog.createAndBook') : t('planner.dialog.book')}
          </Button>
        </Space>
      }
    >
      <Flex vertical gap={12}>
        {refusal !== null ? (
          <Alert
            type="error"
            showIcon
            role="alert"
            message={
              'message' in refusal
                ? refusal.message
                : [
                    ...refusal.conflicts.map((c) => conflictText(c, { resource: resourceName(resourceId), staff: staffName(staffId) })),
                    ...(refusal.freeFrom !== null ? [t('planner.conflict.freeFrom', { time: hhmm(riga(refusal.freeFrom).minutes) })] : []),
                  ].join(' ')
            }
            action={
              'stale' in refusal && refusal.stale === true ? (
                <Button size="small" onClick={() => void reload()}>
                  {t('planner.dialog.reload')}
                </Button>
              ) : !('message' in refusal) && refusal.freeFrom !== null ? (
                <Button
                  size="small"
                  onClick={() => {
                    const at = riga(refusal.freeFrom as string);
                    setDay(at.day);
                    setMinutes(at.minutes);
                    setRefusal(null);
                  }}
                >
                  {t('planner.dialog.startAt', { time: hhmm(riga(refusal.freeFrom).minutes) })}
                </Button>
              ) : undefined
            }
          />
        ) : null}

        {!moving ? (
          <div>
            <Typography.Text strong>{t('planner.dialog.order')}</Typography.Text>
            {fixedOrder !== null ? (
              <div>
                <Typography.Text>{fixedOrder.label}</Typography.Text>
              </div>
            ) : (
              <Flex vertical gap={8} style={{ marginTop: 4 }}>
                {canCreateOrder ? (
                  <Segmented
                    aria-label={t('planner.dialog.order')}
                    value={source}
                    onChange={(v) => setSource(v as 'existing' | 'new')}
                    options={[
                      { value: 'existing', label: t('planner.dialog.existing') },
                      { value: 'new', label: t('planner.dialog.new') },
                    ]}
                  />
                ) : null}
                {source === 'existing' ? (
                  <Select
                    showSearch
                    filterOption={false}
                    aria-label={t('planner.dialog.orderPick')}
                    placeholder={t('planner.dialog.orderPick')}
                    value={orderId}
                    onSearch={setOrderQ}
                    onChange={setOrderId}
                    loading={orders.isFetching}
                    notFoundContent={orders.isError ? describe(orders.error) : undefined}
                    options={(orders.data?.data ?? []).map((o) => ({ value: o.id, label: orderLabel(o) }))}
                  />
                ) : (
                  <Flex vertical gap={8}>
                    <label>
                      <Typography.Text>{t('planner.dialog.vehicle')}</Typography.Text>
                      <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        aria-label={t('planner.dialog.vehicle')}
                        style={{ width: '100%' }}
                        value={vehicle?.id ?? null}
                        notFoundContent={vehicles.isError ? describe(vehicles.error) : undefined}
                        onSearch={setVehicleQ}
                        onChange={(id: string | null) => {
                          const v = vehicles.data?.find((x) => x.id === id);
                          setVehicle(v === undefined ? null : { id: v.id, label: [v.plate, [v.make, v.model].filter(Boolean).join(' ')].filter(Boolean).join(' · '), ownerId: v.ownerCustomerId, ownerName: v.ownerName });
                          if (v?.ownerCustomerId != null && customer === null) setCustomer({ id: v.ownerCustomerId, name: v.ownerName ?? '' });
                        }}
                        options={[
                          ...(vehicle !== null && !(vehicles.data ?? []).some((v) => v.id === vehicle.id) ? [{ value: vehicle.id, label: vehicle.label }] : []),
                          ...(vehicles.data ?? []).map((v) => ({ value: v.id, label: [v.plate, [v.make, v.model].filter(Boolean).join(' '), v.ownerName].filter(Boolean).join(' · ') })),
                        ]}
                      />
                    </label>
                    <label>
                      <Typography.Text>{t('planner.dialog.customer')}</Typography.Text>
                      <Select
                        showSearch
                        filterOption={false}
                        aria-label={t('planner.dialog.customer')}
                        style={{ width: '100%' }}
                        value={customer?.id ?? null}
                        notFoundContent={customers.isError ? describe(customers.error) : undefined}
                        onSearch={setCustomerQ}
                        onChange={(id: string) => {
                          const c = customers.data?.find((x) => x.id === id);
                          setCustomer(c === undefined ? null : { id: c.id, name: c.name });
                        }}
                        options={[
                          ...(customer !== null && !(customers.data ?? []).some((c) => c.id === customer.id) ? [{ value: customer.id, label: customer.name }] : []),
                          ...(customers.data ?? []).map((c) => ({ value: c.id, label: c.name })),
                        ]}
                      />
                    </label>
                    <label>
                      <Typography.Text>{t('planner.dialog.workType')}</Typography.Text>
                      <Select
                        aria-label={t('planner.dialog.workType')}
                        style={{ width: '100%' }}
                        value={workTypeId}
                        onChange={setWorkTypeId}
                        options={(workTypes.data ?? []).map((w) => ({ value: w.id, label: localName(w, i18n.language) }))}
                      />
                    </label>
                    <label>
                      <Typography.Text>{t('planner.dialog.request')}</Typography.Text>
                      <Input.TextArea rows={2} maxLength={2000} aria-label={t('planner.dialog.request')} value={request} onChange={(e) => setRequest(e.target.value)} />
                    </label>
                  </Flex>
                )}
              </Flex>
            )}
          </div>
        ) : null}

        <Flex gap={12} wrap>
          <label style={{ flex: 1, minWidth: 180 }}>
            <Typography.Text>{t('planner.dialog.workplace')}</Typography.Text>
            <Select
              aria-label={t('planner.dialog.workplace')}
              style={{ width: '100%' }}
              value={resourceId ?? ''}
              onChange={(v: string) => setResourceId(v === '' ? null : v)}
              options={[{ value: '', label: t('planner.dialog.noWorkplace') }, ...resources.map((r) => ({ value: r.id, label: r.name }))]}
            />
          </label>
          <label style={{ minWidth: 150 }}>
            <Typography.Text>{t('planner.dialog.day')}</Typography.Text>
            <DatePicker
              aria-label={t('planner.dialog.day')}
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              allowClear={false}
              value={dayjs(day)}
              onChange={(d) => d !== null && setDay(d.format('YYYY-MM-DD'))}
            />
          </label>
          <label style={{ minWidth: 100 }}>
            <Typography.Text>{t('planner.dialog.start')}</Typography.Text>
            <Select showSearch optionFilterProp="label" aria-label={t('planner.dialog.start')} style={{ width: '100%' }} value={minutes} onChange={setMinutes} options={TIMES.map((m) => ({ value: m, label: hhmm(m) }))} />
          </label>
          <label style={{ minWidth: 110 }}>
            <Typography.Text>{t('planner.dialog.duration')}</Typography.Text>
            <Select
              aria-label={t('planner.dialog.duration')}
              style={{ width: '100%' }}
              value={duration}
              onChange={setDuration}
              options={[...new Set([...DURATIONS, duration])].sort((a, b) => a - b).map((m) => ({ value: m, label: durationLabel(m, t) }))}
            />
          </label>
        </Flex>

        <div>
          <Typography.Text strong>
            {t('planner.dialog.performer')} · {t('planner.dialog.freeSpan', {
              span: startsAt === null || endsAt === null ? `${hhmm(minutes)}–?` : `${hhmm(riga(startsAt).minutes)}–${hhmm(riga(endsAt).minutes)}`,
            })}
          </Typography.Text>
          {startsAt === null ? (
            <Alert type="error" showIcon message={t('planner.dialog.noSuchTime')} />
          ) : null}
          {free.isError ? (
            <Alert type="error" showIcon message={describe(free.error)} action={<Button size="small" onClick={() => void free.refetch()}>{t('planner.retry')}</Button>} />
          ) : null}
          {free.isSuccess && freeList.length === 0 ? (
            <div>
              <Typography.Text type="secondary">{t('planner.dialog.nobodyFree')}</Typography.Text>
            </div>
          ) : null}
          <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }} value={staffId ?? ''} onChange={(e) => setStaffId(e.target.value === '' ? null : (e.target.value as string))}>
            {staffChoices.map((s) => (
              <Radio key={s.id} value={s.id}>
                {s.name} {'busy' in s ? <Tag color="orange">{t('planner.dialog.notFree')}</Tag> : <Tag color="green">{t('planner.dialog.free')}</Tag>}
              </Radio>
            ))}
            <Radio value="">{t('planner.dialog.later')}</Radio>
          </Radio.Group>
          {nobody ? (
            <div>
              <Typography.Text type="danger">{t('planner.dialog.chooseOne')}</Typography.Text>
            </div>
          ) : null}
        </div>

        {effectiveOrderId !== null && tires.isError ? (
          <Alert type="error" showIcon message={describe(tires.error)} action={<Button size="small" onClick={() => void tires.refetch()}>{t('planner.retry')}</Button>} />
        ) : null}
        {effectiveOrderId !== null && tires.data !== undefined && (tires.data.tyreFitting || tires.data.sets.length > 0) ? (
          <TireSets data={tires.data} owner={card.data === undefined ? null : { customerId: card.data.customerId, customerName: card.data.customerName, vehicleId: card.data.vehicleId }} />
        ) : null}
        {pendingNew && newTires.isError ? (
          <Alert type="error" showIcon message={describe(newTires.error)} action={<Button size="small" onClick={() => void newTires.refetch()}>{t('planner.retry')}</Button>} />
        ) : null}
        {pendingNew && customer !== null && (newFitting || (newTires.data ?? []).length > 0) && (newTires.isSuccess || !hasCapability('tire-storage.read')) ? (
          <TireSets data={{ tyreFitting: newFitting, sets: newTires.data ?? [] }} owner={{ customerId: customer.id, customerName: customer.name, vehicleId: vehicle?.id ?? null }} />
        ) : null}
      </Flex>
    </Modal>
  );
}

function durationLabel(minutes: number, t: (key: string, o?: Record<string, unknown>) => string): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return t('planner.min', { m });
  return m === 0 ? t('planner.h', { h }) : t('planner.hm', { h, m });
}

/** The client's and the car's tyre sets on storage (P1-7.8) — or the way to receive one. */
function TireSets({
  data,
  owner,
}: {
  data: { tyreFitting: boolean; sets: PlannerTireSet[] };
  /** Whose set would be received — null while not known. */
  owner: { customerId: string; customerName: string | null; vehicleId: string | null } | null;
}): React.JSX.Element {
  const { t } = useTranslation('workOrders');
  const { hasCapability } = useAuth();
  const canOpen = hasCapability('tire-storage.read');
  if (data.sets.length === 0) {
    const receive =
      owner === null
        ? null
        : `/tire-storage/create?${new URLSearchParams({ customerId: owner.customerId, customerName: owner.customerName ?? '', ...(owner.vehicleId !== null && { vehicleId: owner.vehicleId }), returnTo: '/service/planner' }).toString()}`;
    return (
      <Alert
        type="warning"
        showIcon
        message={t('planner.tires.none')}
        action={receive !== null && hasCapability('tire-storage.write') ? <Link to={receive}>{t('planner.tires.receive')}</Link> : undefined}
      />
    );
  }
  return (
    <div>
      <Typography.Text strong>{t('planner.tires.title')}</Typography.Text>
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={data.sets}
        columns={[
          { key: 'number', render: (_v, s) => s.number },
          { key: 'what', render: (_v, s) => `${t(`planner.tires.season.${s.season}`, { defaultValue: s.season })} · ${s.size} · ${t('planner.tires.pcs', { count: s.tireCount })}` },
          { key: 'where', render: (_v, s) => s.path ?? s.locationName },
          {
            key: 'paid',
            render: (_v, s) =>
              s.unpaidBalance === null ? null : s.unpaidBalance > 0 ? (
                <Tag color="red">{t('planner.tires.unpaid', { amount: formatMoney(s.unpaidBalance) })}</Tag>
              ) : (
                <Tag color="green">{t('planner.tires.paid')}</Tag>
              ),
          },
          {
            key: 'open',
            render: (_v, s) =>
              canOpen ? (
                <Link to={`/tire-storage/${s.id}`} aria-label={t('planner.tires.open', { number: s.number })}>
                  {t('planner.tires.openShort')}
                </Link>
              ) : null,
          },
        ]}
        showHeader={false}
      />
    </div>
  );
}
